/**
 * The Home banner for a WhatsApp connection that stopped recording (R5;
 * sketch: docs/design/2026-10-12-realtor-whatsapp-capture-sketch.md §2).
 *
 * Broken (Meta stopped accepting Finishd's token): shown until the realtor
 * disconnects or connects again. Removed in WhatsApp: dismissible, because a
 * connection removed on purpose has nothing else that clears it; the
 * dismissal is kept in this browser for that connection.
 *
 * Shown only where the settings card is: the server has capture switched on
 * and this build carries the Meta config. Nothing shows while the status
 * loads or when it fails to load.
 */

import { MessageCircleWarning } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useWhatsappCapture } from "@/features/realtor/hooks/use-whatsapp";

/** localStorage key holding the `connectedAt` of a dismissed removed connection. */
export const REMOVED_BANNER_DISMISSED_KEY =
	"finishd.whatsappRemovedBannerDismissed";

function readDismissed(): string | null {
	try {
		return window.localStorage.getItem(REMOVED_BANNER_DISMISSED_KEY);
	} catch {
		return null;
	}
}

function writeDismissed(connectedAt: string): void {
	try {
		window.localStorage.setItem(REMOVED_BANNER_DISMISSED_KEY, connectedAt);
	} catch {
		// Storage is unavailable: the banner hides for this visit only.
	}
}

const T = "realtor.dashboard.whatsapp";

export function WhatsappConnectionBanner() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { connection } = useWhatsappCapture();
	const [dismissed, setDismissed] = useState(readDismissed);

	if (!connection) return null;

	const broken = connection.status === "broken";
	const removed =
		connection.status === "disconnected" &&
		connection.disconnectReason === "partner_removed" &&
		dismissed !== connection.connectedAt;
	if (!broken && !removed) return null;

	const dismiss = () => {
		writeDismissed(connection.connectedAt);
		setDismissed(connection.connectedAt);
	};

	return (
		<Card
			className="border-dashboard-warning-border bg-dashboard-warm-050 rounded-xl shadow-[var(--dashboard-shadow-2)]"
			data-testid="whatsapp-connection-banner"
		>
			<CardContent className="flex items-start gap-3 p-5" role="status">
				<div className="shrink-0 rounded-full bg-dashboard-pill-awaiting-bg p-2">
					<MessageCircleWarning className="h-4 w-4 text-dashboard-pill-awaiting" />
				</div>
				<div className="min-w-0 flex-1 space-y-1">
					<p className="text-sm font-semibold text-dashboard-warning">
						{broken ? t(`${T}.brokenTitle`) : t(`${T}.removedTitle`)}
					</p>
					<p className="text-xs text-dashboard-pill-awaiting">
						{broken ? t(`${T}.brokenBody`) : t(`${T}.removedBody`)}
					</p>
					<div className="mt-2 flex flex-col gap-2 sm:flex-row">
						<Button
							size="sm"
							className="h-11 bg-dashboard-accent hover:bg-dashboard-accent/90 text-dashboard-on-fill rounded-[8px] shadow-[var(--dashboard-shadow-primary)] sm:h-8"
							onClick={() => navigate("/realtor/settings")}
						>
							{t(`${T}.openSettings`)}
						</Button>
						{!broken && (
							<Button
								size="sm"
								variant="ghost"
								className="h-11 text-dashboard-muted hover:text-dashboard-text hover:bg-dashboard-surface sm:h-8"
								onClick={dismiss}
							>
								{t(`${T}.dismiss`)}
							</Button>
						)}
					</div>
				</div>
			</CardContent>
		</Card>
	);
}
